/**
 * @fileoverview Filter Debounce Hook
 * 
 * This hook provides debounced filter values for real-time filtering
 * to prevent excessive API calls or processing.
 * 
 * Requirements: 3.7, 10.4, 10.5
 */

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Filter Debounce Hook
 * 
 * @param {ActiveFilter[]} filters - Active filters to debounce
 * @param {number} delay - Debounce delay in milliseconds
 * @returns {ActiveFilter[]} Debounced filters
 */
export const useFilterDebounce = (filters, delay = 300) => {
  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const timeoutRef = useRef(null);
  const previousFiltersRef = useRef(filters);

  /**
   * Check if filters have actually changed
   */
  const filtersChanged = useCallback((newFilters, oldFilters) => {
    if (newFilters.length !== oldFilters.length) {
      return true;
    }

    return newFilters.some((newFilter, index) => {
      const oldFilter = oldFilters[index];
      if (!oldFilter) return true;

      return (
        newFilter.id !== oldFilter.id ||
        JSON.stringify(newFilter.value) !== JSON.stringify(oldFilter.value) ||
        newFilter.operator?.type !== oldFilter.operator?.type ||
        newFilter.enabled !== oldFilter.enabled
      );
    });
  }, []);

  useEffect(() => {
    // Only debounce if filters have actually changed
    if (!filtersChanged(filters, previousFiltersRef.current)) {
      return;
    }

    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    timeoutRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
      previousFiltersRef.current = filters;
    }, delay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [filters, delay, filtersChanged]);

  // Update previous filters reference when component unmounts
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedFilters;
};

/**
 * Advanced Filter Debounce Hook with caching and optimization
 * 
 * @param {ActiveFilter[]} filters - Active filters to debounce
 * @param {Object} options - Debounce options
 * @returns {Object} Debounced filters and utilities
 */
export const useAdvancedFilterDebounce = (filters, options = {}) => {
  const {
    delay = 300,
    maxDelay = 1000,
    enableCaching = true,
    cacheSize = 10
  } = options;

  const [debouncedFilters, setDebouncedFilters] = useState(filters);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const timeoutRef = useRef(null);
  const maxTimeoutRef = useRef(null);
  const cacheRef = useRef(new Map());
  const previousFiltersRef = useRef(filters);
  const lastUpdateRef = useRef(Date.now());

  /**
   * Generate cache key for filters
   */
  const generateCacheKey = useCallback((filterArray) => {
    return JSON.stringify(
      filterArray.map(f => ({
        id: f.id,
        value: f.value,
        operator: f.operator?.type,
        enabled: f.enabled
      }))
    );
  }, []);

  /**
   * Check cache for existing result
   */
  const checkCache = useCallback((filterArray) => {
    if (!enableCaching) return null;

    const key = generateCacheKey(filterArray);
    return cacheRef.current.get(key) || null;
  }, [enableCaching, generateCacheKey]);

  /**
   * Add result to cache
   */
  const addToCache = useCallback((filterArray, result) => {
    if (!enableCaching) return;

    const key = generateCacheKey(filterArray);
    
    // Implement LRU cache
    if (cacheRef.current.size >= cacheSize) {
      const firstKey = cacheRef.current.keys().next().value;
      cacheRef.current.delete(firstKey);
    }
    
    cacheRef.current.set(key, {
      filters: result,
      timestamp: Date.now()
    });
  }, [enableCaching, generateCacheKey, cacheSize]);

  /**
   * Check if filters have changed significantly
   */
  const hasSignificantChange = useCallback((newFilters, oldFilters) => {
    if (newFilters.length !== oldFilters.length) {
      return true;
    }

    // Check for significant changes (not just minor value changes)
    return newFilters.some((newFilter, index) => {
      const oldFilter = oldFilters[index];
      if (!oldFilter) return true;

      // ID or operator changes are always significant
      if (newFilter.id !== oldFilter.id || 
          newFilter.operator?.type !== oldFilter.operator?.type ||
          newFilter.enabled !== oldFilter.enabled) {
        return true;
      }

      // For text filters, only consider changes significant if they're substantial
      if (typeof newFilter.value === 'string' && typeof oldFilter.value === 'string') {
        const lengthDiff = Math.abs(newFilter.value.length - oldFilter.value.length);
        return lengthDiff > 2 || newFilter.value !== oldFilter.value;
      }

      // For other types, any change is significant
      return JSON.stringify(newFilter.value) !== JSON.stringify(oldFilter.value);
    });
  }, []);

  /**
   * Apply debounced update
   */
  const applyDebouncedUpdate = useCallback(() => {
    // Check cache first
    const cached = checkCache(filters);
    if (cached && Date.now() - cached.timestamp < 60000) { // 1 minute cache
      setDebouncedFilters(cached.filters);
      setIsDebouncing(false);
      return;
    }

    // Apply filters and cache result
    setDebouncedFilters(filters);
    addToCache(filters, filters);
    setIsDebouncing(false);
    previousFiltersRef.current = filters;
    lastUpdateRef.current = Date.now();
  }, [filters, checkCache, addToCache]);

  useEffect(() => {
    // Only debounce if filters have changed significantly
    if (!hasSignificantChange(filters, previousFiltersRef.current)) {
      return;
    }

    setIsDebouncing(true);

    // Clear existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }

    // Set debounce timeout
    timeoutRef.current = setTimeout(() => {
      applyDebouncedUpdate();
    }, delay);

    // Set maximum delay timeout to ensure updates don't get stuck
    maxTimeoutRef.current = setTimeout(() => {
      applyDebouncedUpdate();
    }, maxDelay);

    // Cleanup function
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
      }
    };
  }, [filters, delay, maxDelay, hasSignificantChange, applyDebouncedUpdate]);

  /**
   * Force immediate update (bypass debounce)
   */
  const forceUpdate = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (maxTimeoutRef.current) {
      clearTimeout(maxTimeoutRef.current);
    }
    
    applyDebouncedUpdate();
  }, [applyDebouncedUpdate]);

  /**
   * Clear cache
   */
  const clearCache = useCallback(() => {
    cacheRef.current.clear();
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return {
      size: cacheRef.current.size,
      maxSize: cacheSize,
      keys: Array.from(cacheRef.current.keys())
    };
  }, [cacheSize]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (maxTimeoutRef.current) {
        clearTimeout(maxTimeoutRef.current);
      }
    };
  }, []);

  return {
    debouncedFilters,
    isDebouncing,
    forceUpdate,
    clearCache,
    getCacheStats
  };
};
